/*
 * Signed-waiver RECORD — canon §6 signed-record rule (contract): "a stored
 * waiver record is the source of truth … the receipt is a stored record, not
 * a regenerated view", and a signed record can NEVER be edited client-side.
 *
 * Durable home: my-api's POST /me/tickets/:id/waiver (src/data/myApi.ts
 * submitWaiver) — the server record is what gates readiness. This module
 * keeps the participant-visible RECORD (doc titles+versions, declined
 * optional sections, signer, stamp) that the receipt and the read-only
 * re-open render from. Write-once per account per event — store() refuses to
 * overwrite an existing record (fail-loud, no silent mutation).
 *
 * // TODO(my-api): add GET /me/tickets/:id/waiver-record so re-open reads the
 * // server-held record; this device-local copy is the stopgap seam (same
 * // pattern as the wizard's profileStore extras). Cross-entity contract —
 * // escalates via boardroom, not invented here.
 */
import { loadSession } from '../../auth/session'

export interface WaiverRecord {
  eventId: string
  /** Named party — the participant the waiver covers. */
  participant: string
  /** Guardian variant — signer is the guardian, not the participant. */
  guardian: boolean
  /** The signature exactly as typed. */
  signedBy: string
  /** Display stamp, e.g. `9 Aug 2026, 14:05`. */
  when: string
  signedAtIso: string
  docs: Array<{ title: string; version: string }>
  /** Optional section names declined at signing. */
  declined: string[]
}

/** Records are keyed per signed-in account (Cognito sub) per event; `local`
 *  only ever appears without a session (test/dev — a real signature always
 *  has a session, since the expected name comes from the profile). */
const key = (eventId: string): string => {
  const sub = loadSession()?.claims?.sub ?? 'local'
  return `mytrumin.waiverRecord.v1.${sub}.${eventId}`
}

export function loadWaiverRecord(eventId: string): WaiverRecord | null {
  const raw = localStorage.getItem(key(eventId))
  if (!raw) return null
  try {
    return JSON.parse(raw) as WaiverRecord
  } catch {
    return null
  }
}

/** Write-once. Throws rather than overwrite — a signed record is immutable
 *  client-side (canon §6 must-not). */
export function storeWaiverRecord(record: WaiverRecord): void {
  const k = key(record.eventId)
  if (localStorage.getItem(k) !== null) {
    throw new Error('waiver record already exists — signed records cannot be modified')
  }
  localStorage.setItem(k, JSON.stringify(record))
}
